/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Fal.ai Luma Dream Machine & Kling AI Video Generation Driver (Task 6.3.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import type {
  VideoGeneratorDriver,
  VideoProviderCode,
  GenerateVideoOptions,
  VideoGenerationResult,
  GeneratedVideo,
} from '../types';

/**
 * Endpoint configurations for Luma and Kling models on Fal.ai.
 */
export const FAL_VIDEO_MODEL_ENDPOINTS: Record<string, { textToVideo: string; imageToVideo: string }> = {
  'luma-dream-machine': {
    textToVideo: 'fal-ai/luma-dream-machine',
    imageToVideo: 'fal-ai/luma-dream-machine/image-to-video',
  },
  'luma': {
    textToVideo: 'fal-ai/luma-dream-machine',
    imageToVideo: 'fal-ai/luma-dream-machine/image-to-video',
  },
  'fal-ai/luma-dream-machine': {
    textToVideo: 'fal-ai/luma-dream-machine',
    imageToVideo: 'fal-ai/luma-dream-machine/image-to-video',
  },
  'kling-v1': {
    textToVideo: 'fal-ai/kling-video/v1/standard/text-to-video',
    imageToVideo: 'fal-ai/kling-video/v1/standard/image-to-video',
  },
  'kling': {
    textToVideo: 'fal-ai/kling-video/v1/standard/text-to-video',
    imageToVideo: 'fal-ai/kling-video/v1/standard/image-to-video',
  },
  'fal-ai/kling-video': {
    textToVideo: 'fal-ai/kling-video/v1/standard/text-to-video',
    imageToVideo: 'fal-ai/kling-video/v1/standard/image-to-video',
  },
  'fal-ai/kling-video/v1/standard/text-to-video': {
    textToVideo: 'fal-ai/kling-video/v1/standard/text-to-video',
    imageToVideo: 'fal-ai/kling-video/v1/standard/image-to-video',
  },
  'fal-ai/kling-video/v1/standard/image-to-video': {
    textToVideo: 'fal-ai/kling-video/v1/standard/text-to-video',
    imageToVideo: 'fal-ai/kling-video/v1/standard/image-to-video',
  },
  'fal-ai/kling-video/v1.5/pro/text-to-video': {
    textToVideo: 'fal-ai/kling-video/v1.5/pro/text-to-video',
    imageToVideo: 'fal-ai/kling-video/v1.5/pro/image-to-video',
  },
};

/**
 * Base platform credit cost (for 5 seconds) for supported video models.
 */
export const FAL_VIDEO_MODEL_CREDITS: Record<string, number> = {
  'luma-dream-machine': 20,
  'luma': 20,
  'fal-ai/luma-dream-machine': 20,
  'kling-v1': 18,
  'kling': 18,
  'fal-ai/kling-video': 18,
  'fal-ai/kling-video/v1/standard/text-to-video': 18,
  'fal-ai/kling-video/v1/standard/image-to-video': 18,
  'fal-ai/kling-video/v1.5/pro/text-to-video': 30,
};

/**
 * Calculates required credits for a video generation job based on base model rate and 5s duration blocks.
 */
export function calculateFalVideoCredits(model: string, durationSeconds = 5): number {
  const normalized = model.toLowerCase().trim();
  const baseRate =
    FAL_VIDEO_MODEL_CREDITS[normalized] ??
    (normalized.includes('kling') ? 18 : 20);

  const durationBlocks = Math.max(1, Math.ceil(durationSeconds / 5));
  return baseRate * durationBlocks;
}

/**
 * Video generator driver for Luma Dream Machine and Kling AI via Fal.ai queue API.
 */
export class FalVideoDriver implements VideoGeneratorDriver {
  public readonly providerCode: VideoProviderCode = 'fal';

  /**
   * Checks whether this driver supports the requested video model.
   */
  public supportsModel(model: string): boolean {
    const normalized = model.toLowerCase().trim();
    if (Object.prototype.hasOwnProperty.call(FAL_VIDEO_MODEL_ENDPOINTS, normalized)) {
      return true;
    }
    return normalized.includes('luma') || normalized.includes('kling');
  }

  /**
   * Calculates platform credits required for this video generation.
   */
  public calculateCreditCost(model: string, durationSeconds = 5): number {
    return calculateFalVideoCredits(model, durationSeconds);
  }

  /**
   * Executes AI video generation asynchronously via Fal.ai queue endpoints.
   */
  public async generateVideo(options: GenerateVideoOptions): Promise<VideoGenerationResult> {
    const startTime = Date.now();
    const normalizedModel = options.model.toLowerCase().trim();

    if (!options.apiKey || !options.apiKey.trim()) {
      throw new Error('Fal.ai API key is required for video generation');
    }

    if (!options.prompt || !options.prompt.trim()) {
      throw new Error('Prompt is required for video generation');
    }

    const endpointConfig =
      FAL_VIDEO_MODEL_ENDPOINTS[normalizedModel] ??
      (normalizedModel.includes('kling')
        ? {
            textToVideo: 'fal-ai/kling-video/v1/standard/text-to-video',
            imageToVideo: 'fal-ai/kling-video/v1/standard/image-to-video',
          }
        : {
            textToVideo: 'fal-ai/luma-dream-machine',
            imageToVideo: 'fal-ai/luma-dream-machine/image-to-video',
          });

    const isImageToVideo = Boolean(options.imageUrl && options.imageUrl.trim());
    const endpointPath = isImageToVideo ? endpointConfig.imageToVideo : endpointConfig.textToVideo;

    const authHeader = options.apiKey.startsWith('Key ')
      ? options.apiKey
      : `Key ${options.apiKey}`;

    const headers: Record<string, string> = {
      'Authorization': authHeader,
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(options.gatewayOptions?.customHeaders || {}),
    };

    if (options.webhookUrl) {
      headers['X-Fal-Webhook'] = options.webhookUrl;
    }

    // Prepare payload depending on model family (Luma vs Kling)
    let requestPayload: Record<string, unknown>;

    if (normalizedModel.includes('kling')) {
      const durationStr = options.durationSeconds && options.durationSeconds > 5 ? '10' : '5';
      requestPayload = {
        prompt: options.prompt.trim(),
        duration: durationStr,
        aspect_ratio: options.aspectRatio ?? '16:9',
        ...(isImageToVideo ? { image_url: options.imageUrl } : {}),
        ...options.extraParams,
      };
    } else {
      // Luma Dream Machine
      requestPayload = {
        prompt: options.prompt.trim(),
        aspect_ratio: options.aspectRatio ?? '16:9',
        loop: options.loop ?? false,
        ...(isImageToVideo ? { image_url: options.imageUrl } : {}),
        ...options.extraParams,
      };
    }

    const falBaseUrl = 'https://queue.fal.run';
    const submitUrl = `${falBaseUrl}/${endpointPath}`;

    const submitResponse = await fetch(submitUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestPayload),
      signal: options.abortSignal,
    });

    if (!submitResponse.ok) {
      const errorText = await submitResponse.text().catch(() => '');
      throw new Error(
        `Fal.ai video generation submission failed (${submitResponse.status}): ${errorText || submitResponse.statusText}`
      );
    }

    const submitData = (await submitResponse.json()) as Record<string, unknown>;

    // Check if the response already contains the video result (synchronous fast path)
    const directVideo = this.extractVideoFromData(submitData);
    if (directVideo) {
      return {
        video: directVideo,
        model: options.model,
        provider: 'fal',
        creditsUsed: this.calculateCreditCost(options.model, options.durationSeconds),
        executionTimeMs: Date.now() - startTime,
        requestId: typeof submitData.request_id === 'string' ? submitData.request_id : undefined,
        metadata: submitData,
      };
    }

    // Asynchronous queue response: extract polling status or response URL
    const statusUrl = typeof submitData.status_url === 'string' ? submitData.status_url : undefined;
    const responseUrl = typeof submitData.response_url === 'string' ? submitData.response_url : undefined;
    const requestId = typeof submitData.request_id === 'string' ? submitData.request_id : undefined;

    const pollTargetUrl = responseUrl || statusUrl || (requestId ? `https://queue.fal.run/${endpointPath}/requests/${requestId}` : undefined);

    if (!pollTargetUrl) {
      throw new Error('Fal.ai queue response did not include a video URL or polling target URL');
    }

    // Polling loop
    const pollIntervalMs = options.pollIntervalMs ?? 1000;
    const maxPollAttempts = options.maxPollAttempts ?? 60;

    let attempts = 0;
    let completedData: Record<string, unknown> | null = null;

    while (attempts < maxPollAttempts) {
      attempts++;

      if (pollIntervalMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
      }

      if (options.abortSignal?.aborted) {
        throw new Error('Video generation polling was aborted');
      }

      const pollResponse = await fetch(pollTargetUrl, {
        method: 'GET',
        headers: {
          'Authorization': authHeader,
          'Accept': 'application/json',
        },
        signal: options.abortSignal,
      });

      if (!pollResponse.ok) {
        continue;
      }

      const pollData = (await pollResponse.json()) as Record<string, unknown>;
      const status = typeof pollData.status === 'string' ? pollData.status.toUpperCase() : '';

      if (status === 'FAILED' || status === 'ERROR') {
        const detail = typeof pollData.error === 'string' ? pollData.error : JSON.stringify(pollData);
        throw new Error(`Fal.ai video generation failed during queue processing: ${detail}`);
      }

      const polledVideo = this.extractVideoFromData(pollData);
      if (polledVideo) {
        completedData = pollData;
        break;
      }

      // If status_url returned COMPLETED, try fetching response_url if available
      if (status === 'COMPLETED' && responseUrl && pollTargetUrl !== responseUrl) {
        const finalResponse = await fetch(responseUrl, {
          method: 'GET',
          headers: {
            'Authorization': authHeader,
            'Accept': 'application/json',
          },
          signal: options.abortSignal,
        });

        if (finalResponse.ok) {
          const finalData = (await finalResponse.json()) as Record<string, unknown>;
          const finalVideo = this.extractVideoFromData(finalData);
          if (finalVideo) {
            completedData = finalData;
            break;
          }
        }
      }
    }

    if (!completedData) {
      throw new Error(`Fal.ai video generation timed out after ${attempts} polling attempts`);
    }

    const videoItem = this.extractVideoFromData(completedData);
    if (!videoItem) {
      throw new Error('Completed response from Fal.ai did not contain a valid video URL');
    }

    return {
      video: videoItem,
      model: options.model,
      provider: 'fal',
      creditsUsed: this.calculateCreditCost(options.model, options.durationSeconds),
      executionTimeMs: Date.now() - startTime,
      requestId,
      metadata: completedData,
    };
  }

  /**
   * Helper to parse video information from diverse Fal.ai response formats.
   */
  private extractVideoFromData(data: Record<string, unknown>): GeneratedVideo | null {
    // Format 1: { video: { url: "...", content_type: "video/mp4", ... } }
    if (data.video && typeof data.video === 'object') {
      const v = data.video as Record<string, unknown>;
      if (typeof v.url === 'string' && v.url.startsWith('http')) {
        return {
          url: v.url,
          contentType: typeof v.content_type === 'string' ? v.content_type : 'video/mp4',
          fileSizeBytes: typeof v.file_size === 'number' ? v.file_size : undefined,
          width: typeof v.width === 'number' ? v.width : undefined,
          height: typeof v.height === 'number' ? v.height : undefined,
        };
      }
    }

    // Format 2: { video_url: "https://..." }
    if (typeof data.video_url === 'string' && data.video_url.startsWith('http')) {
      return {
        url: data.video_url,
        contentType: 'video/mp4',
      };
    }

    // Format 3: { output: { video: { url: "..." } } }
    if (data.output && typeof data.output === 'object') {
      const out = data.output as Record<string, unknown>;
      return this.extractVideoFromData(out);
    }

    return null;
  }
}
